/**
 * Web Games catalog (#4495).
 *
 * Third-party browser games a host launches onto the stage with one click. Launch
 * reuses the #4482 stage capture and the #4486 Play Together state; this module is
 * only the catalog: what a game is, how it is played, and whether it still loads.
 * Spec: `_docs/Implementation/FunctionalSpecs/4495-web-games-catalog.md`.
 */

export const EXTERNAL_GAME_MODES = ['single', 'coop', 'multiplayer'] as const;
export type ExternalGameMode = (typeof EXTERNAL_GAME_MODES)[number];

export const EXTERNAL_GAME_INPUTS = ['keyboard_mouse', 'gamepad', 'touch'] as const;
export type ExternalGameInput = (typeof EXTERNAL_GAME_INPUTS)[number];

export const EXTERNAL_GAME_DEVICES = ['desktop', 'tablet', 'phone'] as const;
export type ExternalGameDevice = (typeof EXTERNAL_GAME_DEVICES)[number];

/** Written only by the health checker. `unknown` until the first check after a create or URL edit. */
export const EXTERNAL_GAME_HEALTH_STATUSES = ['unknown', 'ok', 'unreachable', 'not_embeddable'] as const;
export type ExternalGameHealthStatus = (typeof EXTERNAL_GAME_HEALTH_STATUSES)[number];

/** Co-op and multiplayer launches also open the game for everyone (#4486); single-player is the host's alone. */
export const opensForEveryone = (mode: ExternalGameMode): boolean => mode !== 'single';

/** What a room sees: enabled, not auto-hidden, no admin notes or health detail. */
export interface ExternalGame {
    id: string;
    slug: string;
    title: string;
    shortDescription: string;
    creatorName: string;
    creatorHandle: string | null;
    creatorUrl: string | null;
    url: string;
    thumbnailUrl: string | null;
    mode: ExternalGameMode;
    minPlayers: number;
    /** `null` = no stated cap. */
    maxPlayers: number | null;
    inputs: ExternalGameInput[];
    devices: ExternalGameDevice[];
    tags: string[];
    featured: boolean;
    sortOrder: number;
    /** ISO timestamp; drives the Newest sort. */
    createdAt: string;
}

/** `GET /api/web-games` (web-next). */
export interface ExternalGamesResponse {
    games: ExternalGame[];
}

/** One health check's verdict, before grooming is applied. */
export type ExternalGameCheckResult =
    | { status: 'ok'; finalUrl: string }
    | { status: 'unreachable' | 'not_embeddable'; reason: string; finalUrl: string | null };

/**
 * #4502 — service API keys for the admin console. A key is scoped: it can reach only the routes of the
 * areas it names, and acts as an editor inside them. `web_games` covers `/api/admin/web-games/**` except key
 * management, which always needs a human session.
 */
export const ADMIN_API_KEY_SCOPES = ['web_games'] as const;
export type AdminApiKeyScope = (typeof ADMIN_API_KEY_SCOPES)[number];
/** Every key starts with this, so a leaked one is recognisable by secret scanners and by the middleware. */
export const ADMIN_API_KEY_PREFIX = 'tgk_';

/** What the admin page and the API show about a key. The key itself is never retrievable after creation. */
export interface AdminApiKeySummary {
    id: string;
    name: string;
    /** First characters of the key, enough to recognise it in a list. */
    keyPrefix: string;
    scopes: AdminApiKeyScope[];
    createdBy: string;
    createdAt: string;
    lastUsedAt: string | null;
    expiresAt: string | null;
    revokedAt: string | null;
}

/** One game in a bulk import: the same fields as creating a game. */
export const WEB_GAME_IMPORT_MAX = 50;
export type WebGameImportStatus = 'created' | 'updated' | 'unchanged' | 'invalid';
export interface WebGameImportResult {
    slug: string | null;
    status: WebGameImportStatus;
    /** Why the entry is invalid, in words an operator can fix. */
    error?: string;
    /** The reachability + frameability check, when one ran. */
    check?: { status: string; reason: string | null };
    /** The game's enabled flag after the import (null when nothing was written). */
    enabled: boolean | null;
    /** Fields that changed on an existing game. */
    changed?: string[];
}

/** Consecutive failed checks before a game is hidden from rooms. The first passing check restores it. */
export const EXTERNAL_GAME_HIDE_AFTER_FAILURES = 3;
/** How often each game is checked. */
export const EXTERNAL_GAME_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;
